import { CommandProps } from '@tiptap/core';
import { TextSelection } from '@tiptap/pm/state';

import { TiptapNodeType } from '../../tiptapTypes';

/**
 * Deletes an empty title when Backspace is pressed within it, moving the cursor to the start
 * of the first task item.
 *
 * Returns false (deferring to the default Backspace behaviour) when the cursor is not in an
 * empty title, so normal in-title character deletion is unaffected.
 */
export const handleEmptyTitleBackspace = ({ state, dispatch }: CommandProps, titleTypeName: string): boolean => {
    const { selection } = state;

    if (!selection.empty) return false;

    const { $from } = selection;

    // Only act when the cursor sits directly inside an empty title node.
    if ($from.parent.type.name !== titleTypeName) return false;
    if ($from.parent.content.size !== 0) return false;

    const titleStart = $from.before();
    const titleEnd = titleStart + $from.parent.nodeSize;

    const transaction = state.tr.delete(titleStart, titleEnd);

    // After the title is removed the task list becomes the first node; place the cursor at the
    // start of its first textblock (searching forward from where the title used to start).
    transaction.setSelection(TextSelection.near(transaction.doc.resolve(titleStart), 1));
    dispatch?.(transaction.scrollIntoView());

    return true;
};

/**
 * Handles Enter within the title by inserting a new empty task at the start of the task list
 * (immediately after the title) and placing the cursor inside it.
 *
 * The title is a single line that can't be split (the document only allows one title), so plain
 * Enter always moves into a fresh first task rather than adding a line — soft line breaks within
 * the title use Shift-Enter instead.
 */
export const handleTitleEnter = ({ state, dispatch }: CommandProps, titleTypeName: string): boolean => {
    const { $from } = state.selection;

    // Only act when the cursor sits within the title node.
    if ($from.parent.type.name !== titleTypeName) return false;

    const taskItemType = state.schema.nodes[TiptapNodeType.taskItem];

    if (!taskItemType) return false;

    const newTask = taskItemType.createAndFill({ checked: false });

    if (!newTask) return false;

    // The task list immediately follows the title, so its content starts one position after the
    // title node ends — i.e. the position of its current first task item.
    const insertPosition = $from.after() + 1;

    const transaction = state.tr.insert(insertPosition, newTask);

    // Place the cursor inside the new task's (empty) paragraph.
    transaction.setSelection(TextSelection.near(transaction.doc.resolve(insertPosition + 2)));
    dispatch?.(transaction.scrollIntoView());

    return true;
};
